import { router } from "@inertiajs/react"
import { Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import type { ClipThumbnail } from "./types"

export function ClipThumbnailPickerDialog({
  open,
  onOpenChange,
  clipId,
  thumbnails,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  clipId: number
  thumbnails: ClipThumbnail[]
}) {
  function pick(thumbnailId: number) {
    router.patch(`/clips/${clipId}/set_primary_thumbnail`, { thumbnail_id: thumbnailId }, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Pick primary thumbnail</DialogTitle>
        </DialogHeader>

        {thumbnails.length === 0 ? (
          <p className="text-sm text-ink-muted">No thumbnails available — re-enrich this clip first.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {thumbnails.map((thumb) => (
              <button
                key={thumb.id}
                type="button"
                onClick={() => pick(thumb.id)}
                className={cn(
                  "group relative aspect-video cursor-pointer overflow-hidden rounded-md border bg-page transition",
                  thumb.is_primary
                    ? "border-accent ring-2 ring-accent-faded"
                    : "border-hairline hover:border-ink-muted",
                )}
                aria-label={`Set primary thumbnail at ${thumb.frame_timestamp_seconds.toFixed(1)}s`}
              >
                {thumb.url ? (
                  <img src={thumb.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-ink-muted">no image</div>
                )}
                {thumb.is_primary && (
                  <span className="absolute right-1 top-1 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-page">
                    <Check className="h-3 w-3" /> Primary
                  </span>
                )}
                <span className="absolute bottom-1 left-1 rounded bg-ink-display/70 px-1.5 py-0.5 text-[10px] font-medium text-page">
                  {thumb.frame_timestamp_seconds.toFixed(1)}s
                </span>
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
